import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import { getSessionAge } from '@rsos/base-utils/metaTags';
import { FullLogo } from '@rsos/components/capstone/Layout/Navigation/common/logo/RapidSOSLogo';
import {
  Box,
  FakeLinkComponent,
  LinkComponent,
  Text,
} from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import {
  type FetchUserResponse,
  fetchUser,
  getTokens,
  logout,
} from '@rsos/sinatra';
import {
  DECRYPT_IRP_TOKEN_SUCCESS,
  PROVIDER_LOGIN_SUCCESS,
  SESSION_START,
  SET_IRP_VERSION,
} from '@rsos/sinatra/src/actions/types';
import {
  captureException,
  captureExceptionWithScope,
  configureUserScope,
} from '@rsos/utils/sentry';
import { notifyError } from '@rsos/utils/toastUtils';
import { trackSSOLogin } from '../../actions/authTrackings';
import { persistor } from '../../store';
import { AppRedirecting } from '../Landing/Landing.styles';
import {
  EMAIL_ERROR_MESSAGE,
  EMAIL_VALIDATION_REGEX,
  LOGIN_BUTTON_TEXT,
  PORTAL_EULA_LINK,
  PRIVACY_POLICY_LINK,
} from '../SSOLogin/SSOLogin';
import {
  EmailField,
  EmailLabel,
  LoginBox,
  LoginButton,
  LoginWrapper,
  StyledForm,
  StyledPage,
} from '../SSOLogin/SSOLogin.styles';
import { requestSSOAuthorization } from '../SSOLogin/authenticateWithSSO';
import authorizeWithSSO from '../SSOLogin/authorizeWithSSO';
import { claimSsoAuthorizationCode } from '../SSOLogin/claimSsoAuthorizationCode';
import determineAppPath from '../SSOLogin/determineAppPath';
import parseQueryString from '../SSOLogin/parseQueryString';
import {
  clearIRPSSOPopupMarker,
  createIRPSSOPopupCallbackMessage,
  IRP_SSO_POPUP_WINDOW_FEATURES,
  IRP_SSO_POPUP_WINDOW_NAME,
  isIRPSSOPopup,
  isIRPSSOPopupCallbackMessage,
  isWindowFramed,
  markIRPSSOPopup,
} from './irpSSOPopup';
import {
  clearPendingIRPLaunch,
  readPendingIRPLaunch,
  savePendingIRPLaunch,
} from './pendingIRPLaunch';

// Browser route for both the CAD launch and the WorkOS callback. Must stay in
// sync with the route in app/App.js, the xFrameOptions allowedPaths in
// package.json/app_manifest.yaml, and the redirect URI registered in each
// WorkOS environment (https://<portal host>/irp-sso, exact match).
export const IRP_SSO_PATH = '/irp-sso';
export const IRP_SSO_LOGIN_ERROR_MESSAGE =
  'Login failed: We could not authenticate you using SSO for iRP. If this error persists, please reach out to your system admin.';
export const IRP_SSO_ACCESS_ERROR_MESSAGE =
  'Login failed: This SSO account is not authorized to launch iRP. Please use a Portal account with iRP access or contact your system admin.';
export const IRP_SSO_QUERY_ERROR_MESSAGE =
  'Login failed: The iRP launch request is missing a valid query.';
export const IRP_SSO_LAUNCH_EXPIRED_MESSAGE =
  'Login failed: The iRP launch request expired or was started in a different browser tab. Please relaunch iRP from your CAD.';
// window.open returns null both when a popup blocker or iframe sandbox stops
// the window and when the embedding CAD page isolates its popups (COOP
// same-origin). In the isolation case Chrome still shows an empty window it
// will not let us control, so the message covers both.
export const IRP_SSO_POPUP_BLOCKED_MESSAGE =
  'Login failed: The secure sign-in window could not be opened from your CAD. Allow pop-ups for RapidSOS and try again, or use "Open sign-in in a new window" below. If an empty window opened, you can close it.';
// Reached when the user closes the window, and also when browser isolation
// cuts the popup off from this panel while it finishes sign-in on its own.
// The wording has to be true in both cases.
export const IRP_SSO_POPUP_CLOSED_MESSAGE =
  'Sign-in not completed here: The sign-in window closed before it returned to your CAD. If iRP opened in that window, you can continue there. Otherwise, try again.';
export const IRP_SSO_POPUP_WAITING_TEXT =
  'Complete sign-in in the RapidSOS window that opened.';
export const IRP_SSO_POPUP_SHOW_TEXT = 'Show sign-in window';
export const IRP_SSO_NEW_WINDOW_LINK_TEXT = 'Open sign-in in a new window';
export const IRP_SSO_POPUP_STALLED_TEXT =
  'RapidSOS could not return this sign-in to your CAD. The CAD panel may have reloaded or closed.';
export const IRP_SSO_POPUP_CONTINUE_TEXT = 'Continue in this window';

const IRP_SSO_POPUP_CLOSE_POLL_MS = 500;
// After relaying, the popup waits for the panel to close it. The panel does
// that synchronously on receipt, so still being open this long later means
// nothing received the callback and its one-time code is still unused.
export const IRP_SSO_POPUP_RELAY_STALL_MS = 10_000;

type PopupStatus = 'idle' | 'waiting' | 'unavailable';

class IRPSSOLoginAPIError extends Error {
  userMessage: string | null;

  // True when the sign-in window itself could not be used (blocked, isolated,
  // or closed early). Only these failures offer the new-window escape hatch;
  // an SSO configuration error would fail the same way in a new window.
  isPopupFailure: boolean;

  constructor(
    message: string,
    userMessage: string | null = null,
    { isPopupFailure = false }: { isPopupFailure?: boolean } = {},
  ) {
    super(message);
    this.name = 'IRPSSOLoginAPIError';
    this.userMessage = userMessage;
    this.isPopupFailure = isPopupFailure;
  }
}

type QueryParam = string | (string | null)[] | null | undefined;
type QueryParams = Record<string, QueryParam>;
// Sent to Sentry. Keep values non-identifying: booleans and phase names only.
// Never the CAD query (a caller phone number), provider-supplied text, the
// WorkOS code, or the state.
type ErrorExtras = Record<string, unknown>;
type ErrorTags = Record<string, string>;
type AuthorizationFailureOptions = {
  clearAuth?: boolean;
};
type IRPSSOState = {
  sinatra?: {
    auth?: {
      tokens?: {
        token?: string | null;
      };
    };
    user?: {
      isLoggedIn?: boolean;
    };
  };
};
type IRPSSODispatch = ThunkDispatch<unknown, unknown, AnyAction>;
type IRPUser = FetchUserResponse & {
  currentRole?: {
    application?: string;
  };
  email?: string;
  profile?: FetchUserResponse['profile'] & {
    email?: string;
  };
};
type SessionStartAction = {
  type: typeof SESSION_START;
  sessionStart: number;
  sessionExpiration: number;
};
type SetIRPVersionAction = {
  type: typeof SET_IRP_VERSION;
  version: string;
};
type IRPLaunchContextAction = {
  type: typeof DECRYPT_IRP_TOKEN_SUCCESS;
  data: {
    rsp_session_token: string;
    query: string;
    isICSP: boolean;
  };
};

const validationSchema = Yup.object({
  email: Yup.string()
    .matches(EMAIL_VALIDATION_REGEX, EMAIL_ERROR_MESSAGE)
    .required(EMAIL_ERROR_MESSAGE),
});

const getSingleQueryParam = (value: QueryParam): string | null | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

const hasMultipleQueryValues = (value: QueryParam): boolean =>
  Array.isArray(value);

const createSessionStartAction = (): SessionStartAction => {
  const now = Math.floor(Date.now() / 1000);
  return {
    type: SESSION_START,
    sessionStart: now,
    sessionExpiration: now + getSessionAge(),
  };
};

const createSetIRPVersionAction = (version: string): SetIRPVersionAction => ({
  type: SET_IRP_VERSION,
  version,
});

const createIRPLaunchContextAction = (
  token: string,
  query: string,
): IRPLaunchContextAction => ({
  // Match the legacy irp_token decrypt result exactly enough for the
  // emergency_data app to bootstrap from irp_access_token and queryURI.
  // This preserves backwards compatibility for all existing iRP UI code
  // without adding a new shared Sinatra API just for this route.
  type: DECRYPT_IRP_TOKEN_SUCCESS,
  data: {
    rsp_session_token: token,
    query,
    isICSP: false,
  },
});

export const isValidIRPQuery = (query: unknown): query is string => {
  // CAD launch payloads intentionally stay narrow: iRP v2 only supports
  // phone-number and alert lookup URIs. Rejecting arbitrary URLs here keeps
  // this browser-entry route from becoming a generic authenticated redirector.
  //
  // The payload character set mirrors deeplink-service RequestSchemaQuery.
  // Keep schemes lowercase because existing iRP consumers parse queryURI with
  // lowercase includes/split checks such as tel:// and alert://. Do not accept
  // lookalike schemes like alert-id:// unless downstream parsers change too.
  return typeof query === 'string' && /^(tel|alert):\/\/[\w\-+@]+$/.test(query);
};

export const createIRPSSORedirectURI = (): string => {
  // WorkOS/Scorpius will return the browser to this route after IdP login.
  // WorkOS rejects any redirect URI that is not an exact registered match, so
  // this must stay the bare route. The CAD query travels separately, bound to
  // the WorkOS state (see pendingIRPLaunch).
  const { protocol, host } = window.location;
  return new URL(`${protocol}//${host}${IRP_SSO_PATH}`).toString();
};

/**
 * Same-origin path that restarts this iRP launch as a normal top-level page.
 * Used as the escape hatch when the sign-in popup cannot be used from inside
 * the CAD frame. Top-level pages take the existing redirect flow, which LASD
 * confirmed works, and iRP then opens in that window.
 */
export const createIRPSSOLaunchPath = (
  query: string,
  email?: string | null,
): string => {
  const params = new URLSearchParams({ query });
  if (email) {
    params.set('email', email);
  }
  return `${IRP_SSO_PATH}?${params.toString()}`;
};

const getSameOriginOpener = (): Window | null => {
  const opener = window.opener as Window | null;
  if (!opener || opener.closed) {
    return null;
  }

  try {
    return opener.location.origin === new URL(createIRPSSORedirectURI()).origin
      ? opener
      : null;
  } catch {
    return null;
  }
};

const shouldRelayPopupCallback = (): boolean => {
  const callbackMessage = createIRPSSOPopupCallbackMessage(
    window.location.search,
  );
  return Boolean(callbackMessage && isIRPSSOPopup() && getSameOriginOpener());
};

/**
 * WorkOS returns the popup to the same registered /irp-sso route. Relay only
 * its one-time callback values to the same-origin opener; the original iframe
 * still owns the launch query, validates state, and exchanges the code.
 */
const IRPSSOPopupCallback = ({
  onRelayUnavailable,
}: {
  onRelayUnavailable: () => void;
}) => {
  const hasRelayed = useRef(false);
  // State (not just the ref) so the stall timer below is re-armed when
  // StrictMode replays effects after the one-time relay already happened.
  const [isRelayed, setIsRelayed] = useState(false);
  const [isStalled, setIsStalled] = useState(false);
  const callbackMessage = useMemo(
    () => createIRPSSOPopupCallbackMessage(window.location.search),
    [],
  );

  useEffect(() => {
    const opener = getSameOriginOpener();
    if (hasRelayed.current || !callbackMessage) {
      return;
    }

    if (!opener) {
      // The opener can be severed by browser isolation between the render-time
      // route decision and this effect. Fall back to exchanging the callback
      // in this top-level window using the launch copy stored in the popup.
      clearIRPSSOPopupMarker();
      onRelayUnavailable();
      return;
    }

    try {
      opener.postMessage(
        callbackMessage,
        new URL(createIRPSSORedirectURI()).origin,
      );
      hasRelayed.current = true;
      setIsRelayed(true);
      clearIRPSSOPopupMarker();
      // Do not close here. postMessage delivery is queued, so closing first can
      // let the iframe's close monitor report a false cancellation. The opener
      // validates source, origin, message shape, and state before closing us.
    } catch {
      // An opener may navigate or become isolated after the origin check. The
      // popup carries the same state-bound launch context, so direct callback
      // processing is safer than leaving the user on an endless spinner.
      clearIRPSSOPopupMarker();
      onRelayUnavailable();
    }
  }, [callbackMessage, onRelayUnavailable]);

  useEffect(() => {
    if (!isRelayed) {
      return undefined;
    }
    // The panel closes this window as soon as it receives the callback, even
    // when it rejects it. Still being open means the panel reloaded, moved on,
    // or never got the message, so do not leave the dispatcher on a spinner.
    const stallTimer = window.setTimeout(
      () => setIsStalled(true),
      IRP_SSO_POPUP_RELAY_STALL_MS,
    );
    return () => window.clearTimeout(stallTimer);
  }, [isRelayed]);

  if (isStalled) {
    // Finishing here is a deliberate user choice rather than automatic, so a
    // panel that is merely slow can never race this window for the one-time
    // code. Continuing exchanges it in this window with the launch copy stored
    // here, and iRP opens in this window.
    return (
      <StyledPage>
        <LoginWrapper>
          <LoginBox>
            <FullLogo height={50} />
            <Box mt={36}>
              <Text textAlign="center">{IRP_SSO_POPUP_STALLED_TEXT}</Text>
            </Box>
            <Box mt={36} width="100%">
              {/* LoginButton is always type="submit"; there is no form here,
                  so the click handler is the only action. */}
              <LoginButton
                onClick={onRelayUnavailable}
                dataName="irp-sso-popup-continue-button"
              >
                {IRP_SSO_POPUP_CONTINUE_TEXT}
              </LoginButton>
            </Box>
          </LoginBox>
        </LoginWrapper>
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      <AppRedirecting />
    </StyledPage>
  );
};

const getStoredAccessToken = (
  storedAccessToken?: string | null,
): string | null => {
  // Already-authenticated users should be able to open another iRP launch
  // without resetting their fixed portal session clock. Prefer Redux, but fall
  // back to Sinatra token storage for reloads where Redux has not hydrated yet.
  if (storedAccessToken) {
    return storedAccessToken;
  }

  try {
    return getTokens()?.token || null;
  } catch {
    return null;
  }
};

const reportAPIError = (
  errorInstance: Error,
  errorExtras: ErrorExtras = {},
  errorTags: ErrorTags = {},
): void => {
  // captureException only accepts the error and silently drops a second
  // argument, so use the scoped variant to actually attach extras and tags.
  captureExceptionWithScope(errorInstance, errorExtras, undefined, errorTags);
};

const IRPSSOLoginFlow = () => {
  const dispatch = useDispatch<IRPSSODispatch>();
  const isAuthorizing = useRef(false);
  const isLaunching = useRef(false);
  const shouldClearAuthOnFailure = useRef(false);
  const popupWindow = useRef<Window | null>(null);
  const popupCloseMonitor = useRef<number | null>(null);
  const hasHandledPopupCallback = useRef(false);
  const storedAccessToken = useSelector(
    (state: IRPSSOState) => state.sinatra?.auth?.tokens?.token,
  );
  const isLoggedIn = useSelector(
    (state: IRPSSOState) => state.sinatra?.user?.isLoggedIn,
  );
  const [isAuthenticationSuccess, setIsAuthenticationSuccess] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Framed sign-in only: whether the dispatcher is waiting on the popup, or
  // the popup could not be used and the new-window escape hatch should show.
  const [popupStatus, setPopupStatus] = useState<PopupStatus>('idle');
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const parsedArgs = useMemo(
    () => parseQueryString(window.location.search) as QueryParams,
    [],
  );
  // Business rule: every launch or callback value that affects authentication
  // must be unambiguous. parseQueryString expands comma-separated values, but
  // repeated raw query params can collapse to the last value, so inspect the
  // original URL before trusting parsedArgs.
  const rawSearchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const initialEmail = getSingleQueryParam(parsedArgs.email) || '';
  const code = getSingleQueryParam(parsedArgs.code);
  const callbackState = getSingleQueryParam(parsedArgs.state);
  const hasCallbackCode = rawSearchParams.has('code');
  const hasCallbackError = rawSearchParams.has('error');
  // WorkOS always returns either a code or an error. `state` alone does not
  // mark a callback, so a CAD launch URL that happens to carry an unrelated
  // `state` param keeps launching from its URL query as it always has.
  const isSSOCallback = hasCallbackCode || hasCallbackError;
  const hasAmbiguousSSOCallback =
    [parsedArgs.code, parsedArgs.state, parsedArgs.error].some(
      hasMultipleQueryValues,
    ) ||
    ['code', 'state', 'error'].some(
      queryParam => rawSearchParams.getAll(queryParam).length > 1,
    );
  // A CAD launch carries the query in the URL. A WorkOS callback arrives on
  // the bare registered route, so its query is recovered only from the
  // launch context bound to the returned state. Any query param on a callback
  // URL is ignored, and a missing launch context is a terminal error rather
  // than a fallback to that param: the callback URL is attacker-craftable,
  // while the stored context can only come from this tab starting the attempt.
  //
  // Never look up launch context with an ambiguous state: the parsed value
  // is an arbitrary pick among the repeated params.
  //
  // Read once at mount: the callback URL cannot change underneath this
  // component, and the result must stay stable after the entry is cleared.
  const [pendingLaunchLookup] = useState(() =>
    isSSOCallback && !hasAmbiguousSSOCallback
      ? readPendingIRPLaunch(callbackState)
      : null,
  );
  const launchQuery = isSSOCallback
    ? (pendingLaunchLookup?.query ?? null)
    : getSingleQueryParam(parsedArgs.query);
  const hasRepeatedLaunchQuery = rawSearchParams.getAll('query').length > 1;
  const hasMultipleLaunchQuery = hasMultipleQueryValues(parsedArgs.query);
  const hasAmbiguousLaunchQuery =
    !isSSOCallback && (hasRepeatedLaunchQuery || hasMultipleLaunchQuery);

  const failAuthorization = useCallback(
    (
      error: unknown,
      extras: ErrorExtras = {},
      { clearAuth = false }: AuthorizationFailureOptions = {},
    ) => {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error));
      const userMessage =
        errorInstance instanceof IRPSSOLoginAPIError
          ? errorInstance.userMessage
          : null;
      // Preserve any earlier decision to clear auth. Some failures happen
      // after Scorpius has returned tokens; those partial credentials must be
      // removed even if a later cleanup path calls this without clearAuth.
      shouldClearAuthOnFailure.current =
        shouldClearAuthOnFailure.current || clearAuth;
      reportAPIError(errorInstance, extras);
      setFailureMessage(userMessage || IRP_SSO_LOGIN_ERROR_MESSAGE);
      setIsAuthenticationSuccess(false);
      setPopupStatus(
        errorInstance instanceof IRPSSOLoginAPIError &&
          errorInstance.isPopupFailure
          ? 'unavailable'
          : 'idle',
      );
    },
    [],
  );

  const stopPopupCloseMonitor = useCallback(() => {
    if (popupCloseMonitor.current !== null) {
      window.clearInterval(popupCloseMonitor.current);
      popupCloseMonitor.current = null;
    }
  }, []);

  const closePopupWindow = useCallback(() => {
    stopPopupCloseMonitor();
    const activePopup = popupWindow.current;
    popupWindow.current = null;
    if (activePopup && !activePopup.closed) {
      activePopup.close();
    }
  }, [stopPopupCloseMonitor]);

  const startPopupCloseMonitor = useCallback(() => {
    stopPopupCloseMonitor();
    popupCloseMonitor.current = window.setInterval(() => {
      const activePopup = popupWindow.current;
      if (!activePopup || !activePopup.closed) {
        return;
      }

      stopPopupCloseMonitor();
      popupWindow.current = null;
      if (!hasHandledPopupCallback.current) {
        clearPendingIRPLaunch();
        failAuthorization(
          new IRPSSOLoginAPIError(
            'SSO popup closed before callback',
            IRP_SSO_POPUP_CLOSED_MESSAGE,
            { isPopupFailure: true },
          ),
          { phase: 'popup_closed' },
        );
      }
    }, IRP_SSO_POPUP_CLOSE_POLL_MS);
  }, [failAuthorization, stopPopupCloseMonitor]);

  useEffect(() => {
    const handlePopupCallback = (event: MessageEvent) => {
      const activePopup = popupWindow.current;
      const expectedOrigin = new URL(createIRPSSORedirectURI()).origin;

      if (
        hasHandledPopupCallback.current ||
        !activePopup ||
        event.source !== activePopup ||
        event.origin !== expectedOrigin ||
        !isIRPSSOPopupCallbackMessage(event.data)
      ) {
        return;
      }

      hasHandledPopupCallback.current = true;
      const {
        code: popupCode,
        error: popupError,
        malformed,
        state,
      } = event.data;
      const pendingLaunch = readPendingIRPLaunch(state);

      if (
        malformed ||
        !state ||
        (!popupCode && !popupError) ||
        !pendingLaunch.query ||
        !isValidIRPQuery(pendingLaunch.query)
      ) {
        closePopupWindow();
        failAuthorization(
          new IRPSSOLoginAPIError(
            'Invalid SSO popup callback',
            pendingLaunch.missReason
              ? IRP_SSO_LAUNCH_EXPIRED_MESSAGE
              : IRP_SSO_LOGIN_ERROR_MESSAGE,
          ),
          {
            hasCode: Boolean(popupCode),
            hasState: Boolean(state),
            hasError: Boolean(popupError),
          },
        );
        return;
      }

      const callbackParams = new URLSearchParams();
      if (popupError) {
        callbackParams.set('error', popupError);
      } else if (popupCode) {
        callbackParams.set('code', popupCode);
      }
      callbackParams.set('state', state);

      closePopupWindow();
      window.location.assign(`${IRP_SSO_PATH}?${callbackParams.toString()}`);
    };

    window.addEventListener('message', handlePopupCallback);
    return () => window.removeEventListener('message', handlePopupCallback);
  }, [closePopupWindow, failAuthorization]);

  useEffect(
    () => () => {
      closePopupWindow();
    },
    [closePopupWindow],
  );

  useEffect(() => {
    // React does not unmount when the page itself goes away, so the cleanup
    // above never runs when the CAD reloads this panel or loads the next call
    // into it. Without this, the popup would finish sign-in and relay to a page
    // with no listener, or complete a launch for a call that is no longer shown.
    // Removing the iframe element entirely does not fire pagehide in Chrome;
    // the popup then sees a closed opener and finishes sign-in on its own.
    const handlePageHide = () => closePopupWindow();
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [closePopupWindow]);

  const focusPopupWindow = useCallback(() => {
    // The popup can open behind a full-screen CAD window.
    try {
      popupWindow.current?.focus();
    } catch {
      // Focus is best effort; the close monitor still tracks the popup.
    }
  }, []);

  const redirectToIRP = useCallback(
    async ({
      token,
      shouldStartSession = false,
    }: {
      token: string | null;
      shouldStartSession?: boolean;
    }) => {
      if (isLaunching.current) {
        return;
      }

      try {
        isLaunching.current = true;
        setIsRedirecting(true);

        if (!token) {
          throw new IRPSSOLoginAPIError('Missing access token for iRP launch');
        }

        if (!isValidIRPQuery(launchQuery)) {
          throw new IRPSSOLoginAPIError('Missing or invalid iRP query');
        }

        // Business rule: do not start a session or write iRP launch context
        // until Scorpius tokens have been proven usable for a Portal user.
        // This keeps unsupported SSO users from leaving partial iRP state that
        // downstream apps would treat as a valid launch.
        const user = (await dispatch(fetchUser())) as IRPUser;
        const applicationPath = determineAppPath(user);
        if (applicationPath !== '/home') {
          // iRP is a RapidSOS Portal/Capstone experience. Users authenticated
          // only for other apps must not get partial iRP state or a new session.
          throw new IRPSSOLoginAPIError(
            'SSO user cannot launch iRP',
            IRP_SSO_ACCESS_ERROR_MESSAGE,
          );
        }

        if (shouldStartSession) {
          dispatch(createSessionStartAction());
        }

        dispatch(createSetIRPVersionAction('2'));
        dispatch(createIRPLaunchContextAction(token, launchQuery));

        try {
          configureUserScope({
            email: user?.profile?.email || user?.email,
            version: process.env.REACT_APP_FE_VERSION || '',
          });
        } catch (error) {
          captureException(error);
        }

        try {
          trackSSOLogin(user?.currentRole?.application);
        } catch (error) {
          captureException(error);
        }

        clearPendingIRPLaunch();
        window.location.assign('/home');
      } catch (error) {
        failAuthorization(
          error,
          { hasQuery: Boolean(launchQuery) },
          { clearAuth: shouldStartSession },
        );
      } finally {
        isLaunching.current = false;
        setIsRedirecting(false);
      }
    },
    [dispatch, failAuthorization, launchQuery],
  );

  useEffect(() => {
    if (hasAmbiguousSSOCallback) {
      // A WorkOS callback should contain only one code/state/error value. If a
      // malformed URL provides multiple values, fail closed instead of choosing
      // one and risking a confusing or mismatched token exchange.
      const error = new IRPSSOLoginAPIError('Ambiguous SSO callback');
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(error, {
        hasCode: Boolean(parsedArgs.code),
        hasState: Boolean(parsedArgs.state),
        hasError: Boolean(parsedArgs.error),
      });
      setFailureMessage(IRP_SSO_LOGIN_ERROR_MESSAGE);
      return;
    }

    if (hasCallbackCode && !code) {
      // A callback with an empty code is malformed. Treat it as a terminal auth
      // failure instead of falling back to the email form and creating a second
      // SSO attempt for a URL that already represents a failed callback.
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(new IRPSSOLoginAPIError('Malformed SSO callback'), {
        hasCode: true,
        hasState: Boolean(parsedArgs.state),
        hasError: Boolean(parsedArgs.error),
      });
      setFailureMessage(IRP_SSO_LOGIN_ERROR_MESSAGE);
      return;
    }

    if (hasCallbackError) {
      // A provider/user error is terminal even when the URL also includes a
      // code. Prefer the explicit error over attempting a token exchange with
      // a potentially stale or mismatched one-time code. The error query value
      // is attacker-controlled, so report only its presence to telemetry.
      shouldClearAuthOnFailure.current = !isLoggedIn;
      reportAPIError(new IRPSSOLoginAPIError('User error'), {
        hasError: true,
      });
      setFailureMessage(IRP_SSO_LOGIN_ERROR_MESSAGE);
      return;
    }

    if (hasAmbiguousLaunchQuery || !isValidIRPQuery(launchQuery)) {
      // Without a valid query this route cannot safely know what CAD event to
      // show after authentication, so fail before SSO starts (launch) or
      // before the code is exchanged (callback). Repeated query params are
      // rejected for the same reason: there must be only one launch target.
      // On a callback, a missing query means the launch context for this state
      // expired, was never stored in this tab, or belongs to another attempt.
      const error = new IRPSSOLoginAPIError(
        isSSOCallback
          ? 'Missing or expired iRP launch for SSO callback'
          : 'Missing or invalid iRP query',
      );
      shouldClearAuthOnFailure.current = !isLoggedIn;
      if (isSSOCallback) {
        // Tag the reason so support can separate expired logins from
        // other-tab or forged callbacks. A recovered but invalid query means
        // the stored entry was tampered with or validation rules changed.
        reportAPIError(
          error,
          { hasState: Boolean(callbackState) },
          {
            irp_sso_launch_miss:
              pendingLaunchLookup?.missReason ?? 'invalid_query',
          },
        );
      } else {
        reportAPIError(error, { hasQuery: Boolean(launchQuery) });
      }
      setFailureMessage(
        isSSOCallback
          ? IRP_SSO_LAUNCH_EXPIRED_MESSAGE
          : IRP_SSO_QUERY_ERROR_MESSAGE,
      );
      return;
    }

    if (isLoggedIn) {
      // Backwards-compatible session behavior: reuse the existing Portal
      // session and only change iRP launch context for the new query.
      redirectToIRP({
        token: getStoredAccessToken(storedAccessToken),
        shouldStartSession: false,
      });
      return;
    }

    if (code) {
      setIsAuthenticationSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resetCallbackURL = () => {
      const resetURL = new URL(
        window.location.pathname,
        window.location.origin,
      );
      if (!hasAmbiguousLaunchQuery && isValidIRPQuery(launchQuery)) {
        resetURL.searchParams.set('query', launchQuery);
      }
      const resetPath = `${resetURL.pathname}${resetURL.search}`;
      // Strip one-time SSO callback fields while preserving a valid CAD query
      // (recovered from launch context on callbacks) so the user can retry
      // without CAD having to open a new browser window.
      window.history.pushState({ path: resetPath }, '', resetPath);
    };

    const resetLoginAttempt = async () => {
      let cleanupFailed = false;
      const reportResetError = (error: unknown, phase: string) => {
        cleanupFailed = true;
        reportAPIError(
          error instanceof Error ? error : new Error(String(error)),
          { phase },
        );
      };

      if (shouldClearAuthOnFailure.current) {
        // Only clear auth state when this route may have persisted a partial
        // SSO callback token. A logged-in dispatcher who opens a bad iRP link
        // should see a launch error, not lose their existing Portal session.
        try {
          await dispatch(logout());
        } catch (error) {
          reportResetError(error, 'logout');
        }

        try {
          await persistor.flush();
        } catch (error) {
          reportResetError(error, 'persistorFlush');
        }

        try {
          clearPortalLocalStorage();
        } catch (error) {
          reportResetError(error, 'clearPortalLocalStorage');
        }

        try {
          if (window.indexedDB) {
            window.indexedDB.deleteDatabase('localforage');
          }
        } catch (error) {
          reportResetError(error, 'deleteIndexedDB');
        }

        if (!cleanupFailed) {
          shouldClearAuthOnFailure.current = false;
        }
      }

      // This attempt is over; a retry stores fresh context under new state.
      clearPendingIRPLaunch();
      resetCallbackURL();
    };

    if (failureMessage) {
      notifyError(failureMessage, {
        autoClose: false,
      });
      void resetLoginAttempt().catch(error => {
        reportAPIError(
          error instanceof Error ? error : new Error(String(error)),
          { phase: 'resetCallbackURL' },
        );
      });
    }
  }, [dispatch, hasAmbiguousLaunchQuery, launchQuery, failureMessage]);

  useEffect(() => {
    const doAuthorization = async () => {
      if (isAuthorizing.current) {
        return;
      }

      if (!code) {
        return;
      }

      // Claim before any await so a remount cannot start a second exchange of
      // the same single use workos code.
      if (!claimSsoAuthorizationCode(code)) {
        return;
      }

      try {
        isAuthorizing.current = true;
        const tokens = await authorizeWithSSO(code, callbackState || undefined);
        if (!tokens?.token) {
          throw new IRPSSOLoginAPIError(
            'Token exchange returned no access token',
          );
        }

        // authorizeWithSSO persists tokens for existing SSO behavior; dispatch
        // PROVIDER_LOGIN_SUCCESS as well so the Redux state is immediately in
        // the same shape as the legacy /sso callback path.
        dispatch({
          type: PROVIDER_LOGIN_SUCCESS,
          tokens,
        });
        // From this point forward, a failure means a partial SSO callback state
        // may exist in local storage or redux-persist and must be cleaned up.
        shouldClearAuthOnFailure.current = true;
        await redirectToIRP({ token: tokens.token, shouldStartSession: true });
      } catch (error) {
        failAuthorization(
          error,
          {
            hasCode: true,
            hasState: Boolean(callbackState),
            hasQuery: Boolean(launchQuery),
          },
          { clearAuth: true },
        );
      } finally {
        isAuthorizing.current = false;
      }
    };

    if (
      isAuthenticationSuccess &&
      code &&
      !hasAmbiguousLaunchQuery &&
      !hasAmbiguousSSOCallback &&
      isValidIRPQuery(launchQuery)
    ) {
      doAuthorization();
    }
  }, [
    callbackState,
    code,
    dispatch,
    failAuthorization,
    hasAmbiguousLaunchQuery,
    hasAmbiguousSSOCallback,
    isAuthenticationSuccess,
    launchQuery,
    redirectToIRP,
  ]);

  return (
    <StyledPage>
      {isRedirecting ? (
        <AppRedirecting />
      ) : (
        <LoginWrapper>
          <LoginBox>
            <FullLogo height={50} />
            <Formik
              initialValues={{ email: initialEmail }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                let authPopup: Window | null = null;
                setPopupStatus('idle');
                setSubmittedEmail(values.email);
                try {
                  if (
                    hasAmbiguousLaunchQuery ||
                    !isValidIRPQuery(launchQuery)
                  ) {
                    throw new IRPSSOLoginAPIError(
                      'Missing or invalid iRP query',
                    );
                  }

                  // Microsoft intentionally blocks interactive sign-in inside
                  // an iframe. Open the popup synchronously from this user
                  // gesture, before the first await, so popup blockers can
                  // distinguish it from an unsolicited window.
                  if (isWindowFramed()) {
                    closePopupWindow();
                    hasHandledPopupCallback.current = false;
                    authPopup = window.open(
                      'about:blank',
                      IRP_SSO_POPUP_WINDOW_NAME,
                      IRP_SSO_POPUP_WINDOW_FEATURES,
                    );
                    if (!authPopup) {
                      throw new IRPSSOLoginAPIError(
                        'SSO popup blocked',
                        IRP_SSO_POPUP_BLOCKED_MESSAGE,
                        { isPopupFailure: true },
                      );
                    }

                    popupWindow.current = authPopup;
                    // The marker survives the top-level WorkOS/IdP round trip
                    // in this popup's RapidSOS sessionStorage, allowing the
                    // bare /irp-sso callback route to select relay mode.
                    markIRPSSOPopup(authPopup.sessionStorage);
                  }

                  // This step only asks Scorpius/WorkOS where to send the
                  // browser. No RapidSOS token is available until the callback
                  // code is exchanged and the returned user is verified.
                  setIsAuthenticationSuccess(true);
                  const { authorizationURL, state } =
                    await requestSSOAuthorization(
                      values.email,
                      createIRPSSORedirectURI(),
                    );

                  if (!authorizationURL) {
                    throw new IRPSSOLoginAPIError('No authorization URL found');
                  }

                  if (!state) {
                    // Without state the callback cannot prove which launch it
                    // belongs to, so there would be no query to recover.
                    throw new IRPSSOLoginAPIError('No SSO state returned');
                  }

                  if (authPopup?.closed) {
                    // The request can outlive a user closing the initially
                    // blank popup. Stop before storing state or trying to
                    // navigate a dead WindowProxy, and preserve a retryable
                    // CAD launch URL in the iframe.
                    throw new IRPSSOLoginAPIError(
                      'SSO popup closed before authorization redirect',
                      IRP_SSO_POPUP_CLOSED_MESSAGE,
                      { isPopupFailure: true },
                    );
                  }

                  savePendingIRPLaunch(state, launchQuery);
                  if (authPopup) {
                    // Keep a copy in the popup as a safe fallback if its opener
                    // relationship is unexpectedly severed. The normal path
                    // still relays to the iframe, which owns the code exchange.
                    savePendingIRPLaunch(
                      state,
                      launchQuery,
                      authPopup.sessionStorage,
                    );
                    authPopup.location.assign(authorizationURL);
                    try {
                      authPopup.focus();
                    } catch {
                      // Focus is cosmetic; authentication can continue.
                    }
                    startPopupCloseMonitor();
                    setPopupStatus('waiting');
                  } else {
                    window.location.assign(authorizationURL);
                  }
                } catch (error) {
                  if (authPopup) {
                    closePopupWindow();
                  }
                  shouldClearAuthOnFailure.current = !isLoggedIn;
                  failAuthorization(error, { hasQuery: Boolean(launchQuery) });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                isSubmitting,
                isValid,
                setFieldValue,
                touched,
                values,
              }) => (
                <StyledForm>
                  <Box width="100%">
                    <Box>
                      <EmailField>
                        <EmailLabel htmlFor="email">Email</EmailLabel>
                        <InputText
                          id="email"
                          name="email"
                          placeholder="email@domain.com"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          shouldErrorTakeSpace
                          error={
                            touched.email && typeof errors.email === 'string'
                              ? errors.email
                              : null
                          }
                        />
                      </EmailField>
                    </Box>
                    <Box mt={36}>
                      <LoginButton
                        isLoadingProp={isAuthenticationSuccess}
                        disabled={isSubmitting || !isValid || !values.email}
                        dataName="irp-sso-button"
                      >
                        {LOGIN_BUTTON_TEXT}
                      </LoginButton>
                    </Box>
                    {popupStatus === 'waiting' ? (
                      <Box mt={16} data-name="irp-sso-popup-waiting">
                        <Text size="small" textAlign="center">
                          {IRP_SSO_POPUP_WAITING_TEXT}{' '}
                          <FakeLinkComponent
                            role="button"
                            tabIndex={0}
                            onClick={focusPopupWindow}
                            onKeyDown={(event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                focusPopupWindow();
                              }
                            }}
                            data-name="irp-sso-popup-show"
                          >
                            {IRP_SSO_POPUP_SHOW_TEXT}
                          </FakeLinkComponent>
                        </Text>
                      </Box>
                    ) : null}
                    {popupStatus === 'unavailable' &&
                    !hasAmbiguousLaunchQuery &&
                    isValidIRPQuery(launchQuery) ? (
                      <Box mt={16} data-name="irp-sso-new-window">
                        <Text size="small" textAlign="center">
                          {/* A plain link click is a navigation, not a script
                              popup, so blockers allow it; LinkComponent opens
                              it with noopener so the new window runs the
                              ordinary top-level flow instead of relay mode. */}
                          <LinkComponent
                            to={createIRPSSOLaunchPath(
                              launchQuery,
                              submittedEmail || initialEmail,
                            )}
                            withBg={false}
                            data-name="irp-sso-new-window-link"
                          >
                            {IRP_SSO_NEW_WINDOW_LINK_TEXT}
                          </LinkComponent>
                        </Text>
                      </Box>
                    ) : null}
                  </Box>
                  <Box mt={36}>
                    <Text size="small" textAlign="center">
                      By logging in, you agree to be bound by the <br />{' '}
                      <LinkComponent to={PORTAL_EULA_LINK} withBg={false}>
                        Unite EULA
                      </LinkComponent>{' '}
                      and{' '}
                      <LinkComponent to={PRIVACY_POLICY_LINK} withBg={false}>
                        Privacy Policy
                      </LinkComponent>
                    </Text>
                  </Box>
                </StyledForm>
              )}
            </Formik>
          </LoginBox>
        </LoginWrapper>
      )}
    </StyledPage>
  );
};

const IRPSSOLogin = () => {
  const [relayUnavailable, setRelayUnavailable] = useState(false);
  const handleRelayUnavailable = useCallback(
    () => setRelayUnavailable(true),
    [],
  );

  return !relayUnavailable && shouldRelayPopupCallback() ? (
    <IRPSSOPopupCallback onRelayUnavailable={handleRelayUnavailable} />
  ) : (
    <IRPSSOLoginFlow />
  );
};

export default IRPSSOLogin;
